import * as Yup from 'yup';

interface RequirementSchemaLang {
  ERROR_INVALID_OPTION: string;
}

interface RequirementSchemaParams {
  professionsWithRoles: Set<string>;
  rolesWithLevels: Set<string>;
  optionCodesByField: Record<string, Set<string>>;
  lang: RequirementSchemaLang;
}

/**
 * The per-org half of the form's validation: which dependent fields are required, and
 * whether the values it holds are still among the options the org offers.
 */
export const buildRequirementSchema = ({
  professionsWithRoles,
  rolesWithLevels,
  optionCodesByField,
  lang,
}: RequirementSchemaParams) => {
  // A stored value can outlive the org's option list; flag it on the field rather than
  // letting the server reject the whole save. A field with no options is one the org
  // hasn't configured — it is hidden, and validating against an empty set would reject
  // every value, including while the options are still loading.
  const isOffered = (fieldKey: string, value?: string | null) => {
    const codes = optionCodesByField[fieldKey];
    return !value || !codes?.size || codes.has(value);
  };

  const singleOption = (fieldKey: string) =>
    Yup.string()
      .nullable()
      .test('offered-option', lang.ERROR_INVALID_OPTION, value => isOffered(fieldKey, value));

  const multiOption = (fieldKey: string) =>
    Yup.array()
      .of(Yup.string())
      .nullable()
      .test('offered-option', lang.ERROR_INVALID_OPTION, values =>
        (values || []).every(value => isOffered(fieldKey, value)),
      );

  // Required only when the value it depends on has children to choose from.
  const requiredForParent = (
    fieldKey: string,
    parentKey: string,
    parentsWithChildren: Set<string>,
  ) =>
    singleOption(fieldKey).test('required-for-parent', 'Required!', function (value) {
      const parent = this.parent?.[parentKey] as string | undefined;
      return !parent || !parentsWithChildren.has(parent) || !!value;
    });

  return Yup.object().shape({
    profession: singleOption('profession'),
    department: singleOption('department'),
    division: multiOption('division'),
    specialty: multiOption('specialty'),
    unit: multiOption('unit'),
    role: requiredForParent('role', 'profession', professionsWithRoles),
    levelOfTraining: requiredForParent('levelOfTraining', 'role', rolesWithLevels),
  });
};
